import { nextTick, onScopeDispose, watch, type Ref } from 'vue';

interface FocusTrapEntry {
    container: Ref<HTMLElement | null>;
    invoker: HTMLElement | null;
}

/** A nested dialog owns keyboard focus until it closes; covered dialogs stay mounted. */
const traps: FocusTrapEntry[] = [];

// Native focusables plus anything explicitly given a tabindex. Negative tabindex values are
// filtered out below rather than in the selector, because a selector cannot express
// "tabindex other than -1".
const FOCUSABLE_SELECTOR = [
    'a[href]',
    'area[href]',
    'button',
    'input',
    'select',
    'textarea',
    'summary',
    'iframe',
    'audio[controls]',
    'video[controls]',
    '[contenteditable]',
    '[tabindex]',
].join(',');

// Read the attribute rather than the `tabIndex` property: everything the selector matches is
// focusable by default, so an absent attribute means 0, and an attribute read gives the same
// answer in a browser and in the test DOM.
const tabIndexOf = (element: HTMLElement): number => {
    const declared = element.getAttribute('tabindex');
    if (declared === null) return 0;

    const parsed = Number.parseInt(declared, 10);
    return Number.isNaN(parsed) ? 0 : parsed;
};

export const getFocusableElements = (container: HTMLElement): HTMLElement[] => {
    return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
        element => !element.hasAttribute('disabled')
            && !element.hasAttribute('hidden')
            && element.getAttribute('aria-hidden') !== 'true'
            && tabIndexOf(element) >= 0,
    );
};

/**
 * The element Tab should land on, cycling within `focusable`. A focus position outside the list —
 * the dialog container itself, or focus that escaped to the page behind — re-enters at the near end.
 */
export const resolveTabTarget = (
    focusable: readonly HTMLElement[],
    activeElement: Element | null,
    backwards: boolean,
): HTMLElement | null => {
    if (focusable.length === 0) return null;

    const currentIndex = focusable.findIndex(element => element === activeElement);
    if (currentIndex === -1) return backwards ? focusable[focusable.length - 1] : focusable[0];

    return focusable[(currentIndex + (backwards ? -1 : 1) + focusable.length) % focusable.length];
};

/**
 * Confines Tab to `container` while `active` is true, moves focus onto the container when the trap
 * engages, and returns focus to whatever invoked it when the trap releases or its scope is disposed.
 * `container` must carry `tabindex="-1"` so it can receive that initial focus without joining the
 * tab order.
 *
 * `onEscape` is handled here, on the same document-level listener, rather than by the container:
 * focus does not stay inside the container for the whole life of a dialog — a click on the backdrop
 * or an element unmounting under the member's focus leaves it on the page body, where a handler
 * bound to the container never fires and the dialog's advertised Escape exit is silently gone.
 */
export const useFocusTrap = (
    container: Ref<HTMLElement | null>,
    active: Ref<boolean>,
    onEscape?: () => void,
    /**
     * Extra roots to treat as part of the trap, resolved on each Tab. A dialog whose contents
     * teleport elsewhere — a popover appended to <body> — is outside `container` in the DOM, so
     * without this the trap sends Tab back to the dialog's first control and the teleported
     * content is unreachable.
     */
    teleported?: () => HTMLElement[],
): { focus: () => void } => {
    const entry: FocusTrapEntry = { container, invoker: null };
    let trapped = false;
    const isTopmost = () => traps.at(-1) === entry;
    const focus = () => {
        if (trapped && isTopmost()) container.value?.focus({ preventScroll: true });
    };

    const onKeydown = (event: KeyboardEvent) => {
        if (!container.value || !isTopmost()) return;

        if (event.key === 'Escape' && onEscape) {
            event.preventDefault();
            event.stopImmediatePropagation();
            onEscape();
            return;
        }

        if (event.key !== 'Tab') return;

        // Tab is swallowed even when nothing inside is focusable, because the browser default would
        // move focus onto the page behind the dialog — precisely what the trap exists to prevent.
        event.preventDefault();
        event.stopImmediatePropagation();

        const focusable = [container.value, ...(teleported?.() ?? [])].flatMap(getFocusableElements);
        resolveTabTarget(focusable, document.activeElement, event.shiftKey)?.focus();
    };

    const trap = () => {
        trapped = true;
        entry.invoker = document.activeElement instanceof HTMLElement ? document.activeElement : null;
        traps.push(entry);
        document.addEventListener('keydown', onKeydown, true);
        // The container is rendered by the same state change that activates the trap, so it only
        // exists once the render caused by that change has flushed.
        void nextTick().then(() => {
            focus();
        });
    };

    const release = () => {
        trapped = false;
        document.removeEventListener('keydown', onKeydown, true);
        const index = traps.indexOf(entry);
        const wasTopmost = isTopmost();
        // If a covered dialog ends first, its successor must restore to the surviving
        // opener instead of a control inside the closed dialog.
        for (const above of traps.slice(index + 1)) {
            if (above.invoker && container.value?.contains(above.invoker)) above.invoker = entry.invoker;
        }
        if (index >= 0) traps.splice(index, 1);
        if (wasTopmost && entry.invoker?.isConnected) entry.invoker.focus();
        entry.invoker = null;
    };

    watch(active, (isActive) => {
        if (typeof document === 'undefined') return;

        if (isActive && !trapped) trap();
        else if (!isActive && trapped) release();
    }, { immediate: true });

    onScopeDispose(() => {
        if (trapped) release();
    });

    return { focus };
};
